import { FleetAreaCode } from '@gb-ticket/shared-types';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiRequestError } from '@/lib/api';
import { PersonChip } from '@/platform/components/person-chip';
import { formatDeDate } from '@/products/fleet/features/fleet-labels';
import {
  fleetPanelClassName,
  fleetTableFrameClassName,
} from '@/products/fleet/features/fleet-panel';
import {
  useCloseFleetAssignment,
  useCreateFleetAssignment,
  useFleetAssignments,
  useFleetPeople,
  useFleetVehicle,
} from '@/products/fleet/features/use-fleet';
import { fleetAreaOn, useFleetAreas } from '@/products/fleet/features/use-fleet-areas';

export function FleetVehicleDriversTab({
  vehicleId,
  manage,
  selfId,
}: {
  vehicleId: string;
  manage: boolean;
  selfId?: string;
}) {
  const vehicle = useFleetVehicle(vehicleId);
  const areas = useFleetAreas();
  const assignments = useFleetAssignments(vehicleId);
  const create = useCreateFleetAssignment();
  const close = useCloseFleetAssignment(vehicleId);
  const [query, setQuery] = useState('');
  const pool = vehicle.data?.usageMode === 'pool';
  const poolOn = fleetAreaOn(areas.isError ? undefined : areas.data, FleetAreaCode.POOL);
  const retired = vehicle.data?.operationalStatus === 'retired';
  const showForm = manage && !retired && (!pool || poolOn);
  const people = useFleetPeople(query, { minLength: 2, enabled: showForm });
  const [userId, setUserId] = useState('');
  const [validFrom, setValidFrom] = useState(new Date().toISOString().slice(0, 10));
  const [override, setOverride] = useState('');
  const [closeDate, setCloseDate] = useState(new Date().toISOString().slice(0, 10));

  const visible = manage
    ? (assignments.data?.items ?? [])
    : (assignments.data?.items ?? []).filter((row) => row.userId === selfId);
  const trimmed = query.trim();
  const searching = trimmed.length >= 2;
  const hits = searching && people.data ? people.data.items : [];
  const showEmpty = searching && !people.isPending && people.isSuccess && hits.length === 0;
  const searchError = searching && people.isError ? people.error : null;

  async function onAssign(event: FormEvent) {
    event.preventDefault();
    if (!userId || !vehicle.data) {
      return;
    }
    try {
      const kind = vehicle.data.usageMode === 'pool' ? 'pool' : 'permanent';
      const result = await create.mutateAsync({
        vehicleId,
        userId,
        kind,
        validFrom,
        validTo: kind === 'pool' ? closeDate : null,
        complianceOverrideReason: override.trim().length >= 10 ? override.trim() : undefined,
      });
      setOverride('');
      toast.success(
        result.warning === 'licenseRestricted'
          ? 'Zuordnung gespeichert (Führerschein eingeschränkt).'
          : 'Zuordnung gespeichert.',
      );
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Zuordnung konnte nicht gespeichert werden.',
      );
    }
  }

  return (
    <div className="space-y-6">
      {assignments.isPending ? (
        <p className="text-sm text-muted-foreground">Zuordnungen werden geladen …</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">Keine Zuordnung in der Historie.</p>
      ) : (
        <ul className={`${fleetTableFrameClassName} divide-y`}>
          {visible.map((row) => (
            <li key={row.id} className="space-y-2 p-3 text-sm">
              <p className="font-medium">
                {row.displayName ? (
                  <PersonChip displayName={row.displayName} profilePhotoUrl={row.profilePhotoUrl} />
                ) : (
                  row.userId
                )}{' '}
                · {row.kind === 'pool' ? 'Pool' : 'Fest'}
              </p>
              <p className="text-muted-foreground">
                {formatDeDate(row.validFrom)}
                {row.validTo ? ` – ${formatDeDate(row.validTo)}` : ' – offen'}
              </p>
              {manage && !row.validTo ? (
                <div className="flex flex-wrap items-end gap-2">
                  <Input
                    type="date"
                    value={closeDate}
                    onChange={(event) => setCloseDate(event.target.value)}
                    className="w-auto"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={close.isPending}
                    onClick={() => {
                      void close
                        .mutateAsync({ id: row.id, body: { validTo: closeDate } })
                        .then(() => toast.success('Zuordnung geschlossen.'))
                        .catch((error: unknown) => {
                          toast.error(
                            error instanceof ApiRequestError
                              ? error.message
                              : 'Schließen fehlgeschlagen.',
                          );
                        });
                    }}
                  >
                    Schließen
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {pool ? (
        <p className="text-sm text-muted-foreground">Poolfahrzeug, kein fester Fahrer.</p>
      ) : null}

      {showForm ? (
        <form className={`${fleetPanelClassName} space-y-3`} onSubmit={onAssign}>
          <h3 className="text-base font-semibold">Zuordnen</h3>
          <Input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setUserId('');
            }}
            placeholder="Person suchen"
            aria-label="Person suchen"
          />
          {searchError ? (
            <p className="text-sm text-destructive">
              {searchError instanceof ApiRequestError
                ? searchError.message
                : 'Die Fahrersuche konnte nicht geladen werden.'}
            </p>
          ) : null}
          {showEmpty ? (
            <p className="text-sm text-muted-foreground">
              Keine Person mit Führerscheinklasse gefunden.
            </p>
          ) : null}
          {hits.length > 0 ? (
            <Select
              value={userId || 'none'}
              onValueChange={(value) => setUserId(value === 'none' ? '' : value)}
            >
              <SelectTrigger id="assign-person" aria-label="Person wählen" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Person wählen</SelectItem>
                {hits.map((person) => (
                  <SelectItem key={person.id} value={person.id}>
                    {person.displayName} ({person.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="assign-from">Beginn</Label>
              <Input
                id="assign-from"
                type="date"
                value={validFrom}
                onChange={(event) => setValidFrom(event.target.value)}
                required
              />
            </div>
            {vehicle.data?.usageMode === 'pool' ? (
              <div className="space-y-1">
                <Label htmlFor="assign-to">Ende</Label>
                <Input
                  id="assign-to"
                  type="date"
                  value={closeDate}
                  onChange={(event) => setCloseDate(event.target.value)}
                  required
                />
              </div>
            ) : null}
          </div>
          <div className="space-y-1">
            <Label htmlFor="assign-override">Compliance-Ausnahme (mind. 10 Zeichen)</Label>
            <Input
              id="assign-override"
              value={override}
              onChange={(event) => setOverride(event.target.value)}
              placeholder="Nur bei Abweichung ausfüllen"
            />
          </div>
          <Button type="submit" disabled={!userId || create.isPending}>
            Zuordnen
          </Button>
        </form>
      ) : null}
    </div>
  );
}
